import BackIcon from '@assets/icons/back.svg?react'
import { MMButton } from '@components'
import LabelValue from '@components/LabelValue'
import {
  extractCheckoutMethodFieldOptionLabel,
  isCheckoutMethodFieldReadOnly,
  prepareCheckoutMethodFieldOptions
} from 'mmfintech-checkout-commons'
import type {
  CheckoutMethodField,
  PaymentOptionResponse,
  UseAdditionalFieldsOutput
} from 'mmfintech-checkout-commons/types'
import { formatMoney, isValidArray, tr, translatePaymentMethodName } from 'mmfintech-commons'
import { ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { shallowEqual, useSelector } from 'react-redux'

type AdditionalFieldsStepProps = {
  selectedPaymentOption: PaymentOptionResponse | null
  paymentMethod: string
  jetonBankSpecialCase: boolean
  fieldValues: UseAdditionalFieldsOutput
  currencyOptions: string[]
  continueCaption: string
  onContinue: () => void
  onCancel?: () => void
  onBack?: () => void
}

const AdditionalFieldsStep = (props: AdditionalFieldsStepProps) => {
  const {
    selectedPaymentOption,
    paymentMethod,
    jetonBankSpecialCase,
    fieldValues,
    currencyOptions,
    continueCaption,
    onContinue,
    onCancel,
    onBack
  } = props

  const { checkoutPayError, checkoutPayFetching, session, sessionError } = useSelector(
    ({ checkout: { checkoutPayError, checkoutPayFetching, session, sessionError } }) => ({
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError
    }),
    shallowEqual
  )

  // to-do:
  // 1. add field descriptions if provided
  // 2. add field.type === 'date' edit
  // 3. add field.name === 'phone' edit

  return (
    <>
      {typeof onBack === 'function' && (
        <div className='back-button-wrapper'>
          <span onClick={onBack} data-test='back-button'>
            <BackIcon />
            <span>{tr('FRONTEND.BUTTONS.BACK', 'Back')}</span>
          </span>
        </div>
      )}

      <div className='payment-method-wrapper' data-test={`pay-with-${paymentMethod}-fields`}>
        {tr('CHECKOUT.PAYMENT.PAY_BY', 'Pay with {{METHOD}}', {
          METHOD: translatePaymentMethodName(paymentMethod)
        })}
      </div>

      {isValidArray(fieldValues.fields) &&
        (!jetonBankSpecialCase || (jetonBankSpecialCase && fieldValues.getValue('bankChoiceId'))) &&
        fieldValues.fields.map((field: CheckoutMethodField) => {
          if (fieldValues.isFieldHidden(field) || field.name === 'bankChoiceId') {
            return null
          }

          if (field.name === 'currency') {
            return (
              <>
                {isCheckoutMethodFieldReadOnly(field) ? (
                  <Input
                    key={field.name}
                    type='text'
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    value={extractCheckoutMethodFieldOptionLabel(field, fieldValues.getValue(field.name))}
                    readOnly={true}
                    hideErrorLine
                  />
                ) : (
                  <Input
                    key={field.name}
                    type='select'
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    {...fieldValues.registerInput(field.name)}
                    options={currencyOptions?.map((currency: string) => ({ value: currency, label: currency }))}
                    hideErrorLine
                  />
                )}
                {Boolean(fieldValues.getValue('currency')) && (
                  <LabelValue
                    label={tr('FRONTEND.BANKING.COMMON.AMOUNT_LABEL', 'Amount')}
                    value={formatMoney(
                      selectedPaymentOption?.currencyAmounts[fieldValues.getValue('currency')],
                      fieldValues.getValue('currency')
                    )}
                    dataTest='amount'
                    enableCopy={false}
                  />
                )}
              </>
            )
          }

          if (field.name === 'documentId' && paymentMethod === 'PIX') {
            return (
              <Input
                key={field.name}
                type='text'
                label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                {...fieldValues.registerShort(field.name)}
                onChange={(name: string, value: string) => fieldValues.setValue(name, value.replace(/[ ./-]/g, ''))}
                required={fieldValues.isFieldRequired(field)}
                readOnly={isCheckoutMethodFieldReadOnly(field)}
                autoComplete='off'
                hideErrorLine
              />
            )
          }

          if (field.type.toLowerCase() === 'select') {
            return isCheckoutMethodFieldReadOnly(field) ? (
              <Input
                key={field.name}
                type='text'
                label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                value={extractCheckoutMethodFieldOptionLabel(field, fieldValues.getValue(field.name))}
                readOnly={true}
                hideErrorLine
              />
            ) : (
              <Input
                key={field.name}
                type='select'
                data-test={`${field.name}-select`}
                options={prepareCheckoutMethodFieldOptions(field)}
                label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                {...fieldValues.registerInput(field.name)}
                hideErrorLine
              />
            )
          }

          return (
            <Input
              key={field.name}
              type={field.sensitive ? 'password' : 'text'}
              label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
              {...fieldValues.registerInput(field.name)}
              readOnly={isCheckoutMethodFieldReadOnly(field)}
              autoComplete='off'
              hideErrorLine
            />
          )
        })}

      <ErrorDisplay error={[sessionError, checkoutPayError]} />

      <div className='buttons'>
        <MMButton
          type='button'
          variant='primary'
          caption={continueCaption}
          onClick={onContinue}
          loading={checkoutPayFetching}
          data-test='button-continue'
          fullWidth
        />

        {Boolean(session?.showCancelButton) && typeof onCancel === 'function' && (
          <MMButton
            type='button'
            variant='secondary'
            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            onClick={onCancel}
            disabled={checkoutPayFetching}
            data-test='button-cancel'
            fullWidth
          />
        )}
      </div>
    </>
  )
}

export default AdditionalFieldsStep
